import { AuthStatus, useAuth, useDocumentQuery } from '@lessonup/client-integration';
import { logger } from '@lessonup/logging-classic';
import { exhaustiveStringTuple } from '@lessonup/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { FeatureFlagsForUserDocument, FeatureFlagsForUserQuery } from './useFeatureFlag.graphql.generated';

export type FeatureFlag = keyof Omit<FeatureFlagsForUserQuery['viewer']['featureFlags'], '__typename' | 'id'>;
export const featureFlags = exhaustiveStringTuple<FeatureFlag>()(
  'editorTopBarCleanup',
  'editorAllPinComponents',
  'editorShowTypographyTypePicker',
  'editorTextShadow',
  'editorBreakEditor',
  'editorSidebarLayoutSize',
  'editorSidebarLayoutPosition',
  'editorLockLayoutSetting',
  'editorUndoRedo',
  'editorPlayerV2',
  'editorPresenterMode',
  'editorOpenQuestion',
  'editorOpenQuestionCorrectAnswers',
  'editorMaiaV2Lessons',
  'editorWordCloud',
  'editorVideoPinComponent',
  'editorAudioPinComponent',
  'editorImageCropping',
  'editorWarningBanner',
  'reportsV2',
  'editorSpinnerPinComponent',
  'playerComponentsMenu',
  'editorEyeDropper',
  'imageGenerator',
  'aiToolboxOverview',
  'aiToolsEditorSidebar',
  'editorTimerPinComponent',
  'editorPollPinV2',
  'editorPollTextPinV2',
  'editorSortingPin',
  'editorAnswerByCategoryPin',
  'fillInTheBlanksPin',
  'editorShapeAllShapes',
  'editorShapePlaygroundSettings',
  'editorPinTheme',
  'editorPinThemeOverrideSettings',
  'editorComponentAnimations',
  'editorDragAndDropPin',
  'shareLessonWithStudents',
  'editorInternalSettings',
  'exportReportButton'
);

export const isValidFeatureFlag = (possibleFeatureFlag: string): possibleFeatureFlag is FeatureFlag =>
  (featureFlags as string[]).includes(possibleFeatureFlag);

export type FeatureFlagValues = Record<FeatureFlag, boolean>;
export type IsFeatureEnabled = (featureFlag: FeatureFlag) => boolean;

export function useFeatureFlags(): {
  featureFlagsValues: FeatureFlagValues;
  isFeatureEnabled: IsFeatureEnabled;
  loading: boolean;
} {
  const auth = useAuth();

  const { data, error, loading } = useDocumentQuery(FeatureFlagsForUserDocument, {
    fetchPolicy: 'cache-first',
    skip: auth.type === AuthStatus.LOGGED_OUT,
  });

  const featureFlagsValues = useMemo(() => {
    return featureFlags.reduce((acc, name) => {
      acc[name] = data?.viewer.featureFlags?.[name] === true;
      return acc;
    }, {} as FeatureFlagValues);
  }, [data]);

  useEffect(() => {
    if (error) logger.error(error);
  }, [error]);

  const isFeatureEnabled: IsFeatureEnabled = useCallback(
    (featureFlag) => {
      return featureFlagsValues[featureFlag] === true;
    },
    [featureFlagsValues]
  );

  return {
    featureFlagsValues,
    isFeatureEnabled,
    loading,
  };
}
